import type { AcademicTerm, ClassRole, Classroom } from "@classroom/domain";
import { useMemo, useState } from "react";
import { useTranslation } from "./i18n";
import { useDialogFocus } from "./lib/accessibility";
import {
  type AdminRecordKind,
  buildAdminRecordReport,
  ROLE_RECORD_KINDS,
  recordKindsForRole,
  SHARED_RECORD_KINDS,
} from "./lib/adminRecordExport";
import { type ExportRange, type RecordExportSource, validateExportRange } from "./lib/localRecordExport";
import { RECORD_EXPORT_FORMATS, type RecordFileFormat } from "./lib/recordExportFormats";
import { exportRecordReport, type RecordReport } from "./lib/recordExports";
import RecordActionButton from "./RecordActionButton";
import RecordPdfPreview from "./RecordPdfPreview";
import StudentPagination, { useStudentPagination } from "./StudentPagination";

export default function AdminRecordExportPanel({
  classes,
  classId,
  source,
  terms,
}: {
  classes: Classroom[];
  classId?: string;
  source: RecordExportSource;
  terms: AcademicTerm[];
}) {
  const { t, i18n } = useTranslation("adminRecords");
  const { t: exportText } = useTranslation("localExport");
  const initialClass = classes.find((item) => item.id === classId) || classes[0];
  const [role, setRole] = useState<ClassRole>(initialClass?.classRole || "teaching");
  const [selectedIds, setSelectedIds] = useState<string[]>(initialClass ? [initialClass.id] : []);
  const roleClasses = classes.filter((item) => item.classRole === role);
  const selectedClasses = useMemo(
    () => classes.filter((item) => item.classRole === role && selectedIds.includes(item.id)),
    [classes, role, selectedIds],
  );
  const [termId, setTermId] = useState("");
  const [range, setRange] = useState<ExportRange>({});
  const [included, setIncluded] = useState<AdminRecordKind[]>([]);
  const [format, setFormat] = useState<RecordFileFormat>("xlsx");
  const [previewTitle, setPreviewTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [pdfReport, setPdfReport] = useState<RecordReport | null>(null);
  const pdfDialogRef = useDialogFocus<HTMLElement>(Boolean(pdfReport), () => {
    if (!busy) setPdfReport(null);
  });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const term = terms.find((item) => item.id === termId);
  const rangeError = validateExportRange(range, term);
  const report = useMemo(() => {
    if (rangeError || !included.length) return undefined;
    return buildAdminRecordReport(selectedClasses, source, included, range, term);
  }, [selectedClasses, source, included, range, term, rangeError, i18n.resolvedLanguage]);
  const sheet = report?.sheets.find((item) => item.title === previewTitle) || report?.sheets[0];
  const rows = useMemo(() => (sheet?.rows || []).map((cells, index) => ({ cells, index })), [sheet]);
  const pagination = useStudentPagination(
    rows,
    JSON.stringify([sheet?.title, selectedIds, role, termId, range, included]),
  );
  const invalid = !report?.sheets.length || Boolean(rangeError);
  function clearResult() {
    setError("");
    setNotice("");
  }
  const from = range.from || term?.startDate;
  const to = range.to || term?.endDate;
  const dateSummary =
    from && to
      ? t("dateRange", { from, to })
      : from
        ? t("fromOnly", { date: from })
        : to
          ? t("toOnly", { date: to })
          : t("allDates");
  function renderRecordChoices(kinds: AdminRecordKind[]) {
    return (
      <div className="admin-export-kind-grid">
        {kinds.map((kind) => (
          <label key={kind}>
            <input
              type="checkbox"
              aria-label={t(`kinds.${kind}`)}
              checked={included.includes(kind)}
              onChange={(event) => {
                setIncluded((current) =>
                  event.target.checked ? [...current, kind] : current.filter((item) => item !== kind),
                );
                clearResult();
              }}
            />
            <span>
              <strong>{t(`kinds.${kind}`)}</strong>
              <small>{t(`descriptions.${kind}`)}</small>
            </span>
          </label>
        ))}
      </div>
    );
  }
  async function performExport() {
    if (busy || invalid || !report) return;
    clearResult();
    if (format === "pdf") {
      setPdfReport(report);
      return;
    }
    setBusy(true);
    try {
      await exportRecordReport(report, format);
      setNotice(t("downloaded"));
    } catch {
      setError(t("failed"));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="admin-record-export" aria-label={t("exportTitle")} data-save-state={busy ? "saving" : "saved"}>
      <div className="admin-export-layout">
        <div className="paper-card admin-export-settings">
          <fieldset disabled={busy} className="admin-export-section">
            <legend>{t("chooseClasses")}</legend>
            <div className="admin-export-role-switch" role="radiogroup" aria-label={t("role")}>
              {(["teaching", "homeroom"] as const).map((value) => (
                <label key={value}>
                  <input
                    type="radio"
                    name="admin-export-role"
                    checked={role === value}
                    onChange={() => {
                      setRole(value);
                      setSelectedIds(
                        classes
                          .filter((item) => item.classRole === value)
                          .slice(0, 1)
                          .map((item) => item.id),
                      );
                      setIncluded([]);
                      setPreviewTitle("");
                      clearResult();
                    }}
                  />
                  <span>{t(`roles.${value}`)}</span>
                </label>
              ))}
            </div>
            <div className="admin-export-selection-actions">
              <button
                type="button"
                className="text-action"
                disabled={!roleClasses.length}
                onClick={() => {
                  setSelectedIds(roleClasses.map((item) => item.id));
                  clearResult();
                }}
              >
                {t("allRoleClasses")}
              </button>
              <button
                type="button"
                className="text-action"
                disabled={!selectedClasses.length}
                onClick={() => {
                  setSelectedIds([]);
                  clearResult();
                }}
              >
                {t("clearClasses")}
              </button>
            </div>
            <div className="admin-export-class-grid" role="group" aria-label={t("class")}>
              {roleClasses.map((item) => (
                <label key={item.id}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(item.id)}
                    onChange={(event) => {
                      setSelectedIds((current) =>
                        event.target.checked ? [...current, item.id] : current.filter((id) => id !== item.id),
                      );
                      clearResult();
                    }}
                  />
                  <span>{item.name}</span>
                </label>
              ))}
            </div>
            {!roleClasses.length && <p className="admin-export-note">{t("noClasses")}</p>}
          </fieldset>
          <fieldset disabled={busy} className="admin-export-section admin-export-records">
            <legend>{t("recordsStep")}</legend>
            <div className="admin-export-selection-actions">
              <button
                type="button"
                className="text-action"
                onClick={() => {
                  setIncluded(recordKindsForRole(role));
                  clearResult();
                }}
              >
                {t("all")}
              </button>
              <button
                type="button"
                className="text-action"
                disabled={!included.length}
                onClick={() => {
                  setIncluded([]);
                  clearResult();
                }}
              >
                {t("clear")}
              </button>
            </div>
            <h4>{t("roleRecords", { role: t(`roles.${role}`) })}</h4>
            {renderRecordChoices(ROLE_RECORD_KINDS[role])}
            <h4>{t("sharedRecords")}</h4>
            {renderRecordChoices(SHARED_RECORD_KINDS)}
          </fieldset>
          <fieldset disabled={busy} className="admin-export-section admin-export-scope">
            <legend>{t("dateStep")}</legend>
            <label className="admin-export-term">
              {t("term")}
              <select
                aria-label={t("term")}
                value={termId}
                onChange={(event) => {
                  setTermId(event.target.value);
                  setRange({});
                  clearResult();
                }}
              >
                <option value="">{t("allDates")}</option>
                {[...terms]
                  .sort((a, b) => b.startDate.localeCompare(a.startDate))
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.schoolYear} · {item.name}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              {exportText("from")}
              <input
                type="date"
                value={range.from || ""}
                min={term?.startDate}
                max={term?.endDate}
                onChange={(event) => {
                  setRange({ ...range, from: event.target.value });
                  clearResult();
                }}
              />
            </label>
            <label>
              {exportText("to")}
              <input
                type="date"
                value={range.to || ""}
                min={term?.startDate}
                max={term?.endDate}
                onChange={(event) => {
                  setRange({ ...range, to: event.target.value });
                  clearResult();
                }}
              />
            </label>
          </fieldset>
        </div>
        <aside className="paper-card admin-export-summary" aria-label={t("summary")}>
          <div className="admin-export-summary-header">
            <h3>{t("summary")}</h3>
            <span className="admin-export-role-badge">{t(`roles.${role}`)}</span>
          </div>
          <div className="admin-export-summary-values" aria-live="polite" aria-atomic="true">
            <h4>{t("selectedClasses", { count: selectedClasses.length })}</h4>
            {selectedClasses.length ? (
              <ul className="admin-export-summary-classes">
                {selectedClasses.map((item) => (
                  <li key={item.id}>{item.name}</li>
                ))}
              </ul>
            ) : (
              <p>{t("noSelectedClasses")}</p>
            )}
            <h4>{t("selectedRecords", { count: included.length })}</h4>
            {included.length ? (
              <ul className="admin-export-summary-records">
                {recordKindsForRole(role)
                  .filter((kind) => included.includes(kind))
                  .map((kind) => (
                    <li key={kind}>{t(`kinds.${kind}`)}</li>
                  ))}
              </ul>
            ) : (
              <p>{t("noSelectedRecords")}</p>
            )}
            <h4>{t("selectedDates")}</h4>
            <p>
              {term && (
                <strong>
                  {term.schoolYear} · {term.name}
                  <br />
                </strong>
              )}
              {dateSummary}
            </p>
          </div>
          <fieldset disabled={busy} className="admin-export-formats">
            <legend>{t("format")}</legend>
            {RECORD_EXPORT_FORMATS.map((value) => (
              <label key={value}>
                <input
                  type="radio"
                  name="admin-export-format"
                  aria-label={`${exportText(`formats.${value}`)} (.${value})`}
                  checked={format === value}
                  onChange={() => {
                    setFormat(value);
                    clearResult();
                  }}
                />
                <span>
                  <strong>{exportText(`formats.${value}`)}</strong>
                  <small>{t(`formatHints.${value}`)}</small>
                </span>
              </label>
            ))}
          </fieldset>
          <RecordActionButton
            action="export"
            className="admin-export-download"
            disabled={busy || invalid}
            onClick={() => void performExport()}
          >
            {busy
              ? t("working")
              : t("downloadSelection", {
                  format: exportText(`formats.${format}`),
                  classes: selectedClasses.length,
                  records: included.length,
                })}
          </RecordActionButton>
          {rangeError && (
            <p role="alert" className="form-error">
              {exportText(`errors.${rangeError}`)}
            </p>
          )}
          {error && (
            <p role="alert" className="form-error">
              {error}
            </p>
          )}
          {notice && <p role="status">{notice}</p>}
        </aside>
      </div>
      {sheet && report && (
        <section className="paper-card admin-export-preview" aria-label={t("preview")}>
          <header>
            <h3>{t("preview")}</h3>
            <label>
              {exportText("sheet")}
              <select
                aria-label={exportText("sheet")}
                value={sheet.title}
                disabled={busy}
                onChange={(event) => setPreviewTitle(event.target.value)}
              >
                {report.sheets.map((item) => (
                  <option key={item.title} value={item.title}>
                    {item.title}
                  </option>
                ))}
              </select>
            </label>
          </header>
          {/* biome-ignore lint/a11y/noNoninteractiveTabindex: Keyboard focus allows horizontal table scrolling. */}
          <div className="local-export-table-scroll" tabIndex={0} aria-label={t("preview")}>
            <table className="local-export-table" aria-label={t("preview")}>
              <thead>
                <tr>
                  {sheet.headers.map((header) => (
                    <th key={header} scope="col">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pagination.items.map(({ cells, index }) => (
                  <tr key={index}>
                    {cells.map((cell, column) => (
                      <td key={sheet.headers[column]}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!rows.length && <p className="admin-export-note">{t("emptyRecords")}</p>}
          <StudentPagination {...pagination} disabled={busy} />
        </section>
      )}
      {pdfReport && (
        <div
          className="record-export-backdrop"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget && !busy) setPdfReport(null);
          }}
        >
          <section
            ref={pdfDialogRef}
            tabIndex={-1}
            className="paper-card record-export-dialog local-export-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-pdf-preview-title"
          >
            <header className="local-export-header">
              <h2 id="admin-pdf-preview-title">{pdfReport.title}</h2>
              <button
                type="button"
                className="record-export-close"
                aria-label={exportText("close")}
                disabled={busy}
                onClick={() => setPdfReport(null)}
              >
                ×
              </button>
            </header>
            <p className="local-export-scope">{pdfReport.className}</p>
            <RecordPdfPreview report={pdfReport} onBusyChange={setBusy} />
          </section>
        </div>
      )}
    </section>
  );
}
